/**
 * World Courier portal add-package selects (values from WC `select` option `value`s).
 * Order and label text match the WC UI dropdowns (see product screenshots).
 */

export interface WcSelectOption {
  value: string;
  label: string;
}

/** Order matches WC “Select Temperature Range / Refrigerant” menu. */
export const worldCourierTemperatureRangeSelectOptions: WcSelectOption[] = [
  { value: 'Ambfrz', label: 'Combined Ambient/Frozen (Non-WC)' },
  { value: 'Ambient', label: 'Ambient (No specified range)' },
  {
    value: 'Controlled Ambient',
    label: 'Controlled Ambient +15C to +25C',
  },
  { value: 'Cryogenic', label: 'Cryogenics -190C to -150C' },
  { value: 'Deep', label: 'Deep Frozen Dry Ice -70C [+/-10C]' },
  { value: 'Dry Ice', label: 'Frozen Dry Ice -80C to -20C' },
  { value: 'Elevated', label: 'Elevated +35 [+/-5C]' },
  { value: 'Frozen25', label: 'Frozen -25C to -15C' },
  { value: 'Frozen40', label: 'Frozen -40C [+/-10C]' },
  { value: 'Frozen50', label: 'Frozen -50C [+/-10C]' },
  { value: 'NA', label: 'NA' },
  { value: 'Other', label: 'Other (Non-WC)' },
  {
    value: 'Refamb',
    label: 'Combined Refrigerated/Ambient (Non-WC)',
  },
  {
    value: 'Reffrz',
    label: 'Combined Refrigerated/Frozen (Non-WC)',
  },
  { value: 'Refrigerated', label: 'Refrigerated +2C to +8C' },
];

/** Temperature option values that require dry ice supply + weight in the WC portal. */
export const WC_TEMPERATURES_REQUIRING_DRY_ICE = new Set<string>([
  'Deep',
  'Dry Ice',
]);

/**
 * WC “Select Dry Ice Supply” (`#add-package-N_dry-ice-supply_input`) — portal `<option value>` equals label text.
 */
export const worldCourierDryIceSupplySelectOptions: WcSelectOption[] = [
  { value: 'WC To Supply Dry Ice', label: 'WC To Supply Dry Ice' },
  { value: 'Use My Own Dry Ice', label: 'Use My Own Dry Ice' },
];

/** WC portal fixes this when a GDI “with N kg Dry” package is selected (supply control not user-editable). */
export const WC_BUNDLED_PACKAGE_DRY_ICE_SUPPLY =
  'WC To Supply Dry Ice' as const;

/**
 * WC “Select Extra Supplies” (`#add-package-N_temperature-probe_input`) — portal `<option value>`.
 */
export const worldCourierExtraSuppliesSelectOptions: WcSelectOption[] = [
  // Temperature-specific (highlighted) options
  { value: '85', label: 'Dry Ice Frozen Dry Ice -80C to -20C' },
  {
    value: '331',
    label: 'TT Ultra Single Use Controlled Ambient +15C to +25C',
  },
  { value: '332', label: 'TT Ultra Single Use Frozen -25C to -15C' },
  { value: '333', label: 'TT Ultra Single Use Frozen Dry Ice -80C to -20C' },
  {
    value: '334',
    label: 'TT Ultra Single Use Deep Frozen Dry Ice -70C [+/-10C]',
  },
  { value: '336', label: 'TT Ultra Single Use Refrigerated +2C to +8C' },
  { value: '449', label: 'DS DV10 ModPak' },
  { value: '450', label: 'DS GPS DV10 LID Cryogenics -190C to -150C' },
  // Common options (available for all temperature ranges)
  { value: '210', label: 'IATA 650 Extra Large Bag/Envelope' },
  { value: '211', label: 'IATA 650 Large Bag/Envelope' },
  { value: '212', label: 'IATA 650 Medium Bag/Envelope' },
  { value: '214', label: 'IATA 650 Small Bag/Envelope' },
  { value: '315', label: 'TM Non-WC' },
  { value: '335', label: 'TT Ultra Single Use' },
  { value: '337', label: 'Vial Box' },
  { value: '393', label: 'Cryoboxes WC' },
];

/** Extra supply values available for every temperature range. */
export const WC_COMMON_EXTRA_SUPPLY_VALUES = new Set<string>([
  '210',
  '211',
  '212',
  '214',
  '315',
  '335',
  '337',
  '393',
]);

/**
 * Temperature-range-specific extra supply values (highlighted in the portal).
 * Keys are `temperature_range_refrigerant` portal values.
 */
export const WC_TEMPERATURE_EXTRA_SUPPLIES: Record<string, string[]> = {
  'Controlled Ambient': ['331'],
  Cryogenic: ['449', '450'],
  Deep: ['334'],
  'Dry Ice': ['85', '333'],
  Frozen25: ['332'],
  Refrigerated: ['336'],
};

/**
 * Returns all valid extra supply values for a given temperature range.
 * Includes both temperature-specific (highlighted) and common options.
 */
export function getValidExtraSupplyValuesForTemperature(
  temperatureRaw: string | null | undefined
): Set<string> {
  const temp = String(temperatureRaw ?? '').trim();
  const specific: string[] = WC_TEMPERATURE_EXTRA_SUPPLIES[temp] ?? [];
  return new Set(specific.concat(Array.from(WC_COMMON_EXTRA_SUPPLY_VALUES)));
}

/**
 * Invariant: every value in WC_COMMON_EXTRA_SUPPLY_VALUES and WC_TEMPERATURE_EXTRA_SUPPLIES
 * must exist in worldCourierExtraSuppliesSelectOptions. Throws at module load time so
 * misconfiguration is caught immediately in dev/test rather than silently hiding options.
 */
(function assertExtraSupplyConfiguration() {
  const knownValues = new Set(
    worldCourierExtraSuppliesSelectOptions.map((o) => o.value)
  );
  const referenced = new Set<string>(Array.from(WC_COMMON_EXTRA_SUPPLY_VALUES));
  Object.values(WC_TEMPERATURE_EXTRA_SUPPLIES).forEach((values) =>
    values.forEach((v) => referenced.add(v))
  );
  const missing = Array.from(referenced).filter((v) => !knownValues.has(v));
  if (missing.length > 0) {
    throw new Error(
      `WC extra supply config references values missing from worldCourierExtraSuppliesSelectOptions: ${missing.join(', ')}`
    );
  }
})();

/** Order matches WC “Select your packaging option” menu. */
export const worldCourierPackageNameSelectOptions: WcSelectOption[] = [
  {
    value: '174',
    label: 'GTC12L Controlled Ambient +15C to +25C',
  },
  {
    value: '178',
    label: 'GTC28L Controlled Ambient +15C to +25C',
  },
  {
    value: '186',
    label: 'GTC56L Controlled Ambient +15C to +25C',
  },
  {
    value: '190',
    label: 'GTC96L Controlled Ambient +15C to +25C',
  },
  { value: '17', label: 'World Courier to assign packaging' },
  {
    value: '138',
    label: 'GDI15L Frozen -80C to -20C with 10kg Dry Ice',
  },
  {
    value: '139',
    label: 'GDI15L Frozen -80C to -20C Excluding Dry Ice',
  },
  {
    value: '143',
    label: 'GDI30L Frozen -80C to -20C with 25kg Dry',
  },
  {
    value: '144',
    label: 'GDI30L Frozen -80C to -20C Excluding Dry Ice',
  },
  {
    value: '148',
    label: 'GDI45L Frozen -80C to -20C with 35kg Dry',
  },
  {
    value: '149',
    label: 'GDI45L Frozen -80C to -20C Excluding Dry Ice',
  },
  {
    value: '153',
    label: 'GDI7L Frozen -80C to -20C with 4kg Dry Ice',
  },
  {
    value: '154',
    label: 'GDI7L Frozen -80C to -20C Excluding Dry Ice',
  },
  {
    value: '158',
    label: 'GDI80L Frozen -80C to -20C with 45kg Dry Ice',
  },
  {
    value: '159',
    label: 'GDI80L Frozen -80C to -20C Excluding Dry Ice',
  },
  { value: '175', label: 'GTC12L Frozen -25C to -15C' },
  { value: '177', label: 'GTC12L Refrigerated +2C to +8C' },
  { value: '179', label: 'GTC28L Frozen -25C to -15C' },
  { value: '181', label: 'GTC28L Refrigerated +2C to +8C' },
  { value: '187', label: 'GTC56L Frozen -25C to -15C' },
  { value: '189', label: 'GTC56L Refrigerated +2C to +8C' },
  { value: '191', label: 'GTC96L Frozen -25C to -15C' },
  { value: '193', label: 'GTC96L Refrigerated +2C to +8C' },
  {
    value: '448',
    label: 'Dry Shipper DV10 Hard Case Cryogenics -190C to -150C',
  },
  {
    value: '89',
    label: 'Cryoport Dry Vapour Shipper Cryogenics -190C to -150C',
  },
  { value: '140', label: 'GDI15L Deep Frozen -70C [+/-10C] with 10kg Dry Ice' },
  { value: '141', label: 'GDI15L Deep Frozen -70C [+/-10C] Excluding Dry Ice' },
  { value: '145', label: 'GDI30L Deep Frozen -70C [+/-10C] with 25kg Dry Ice' },
  {
    value: '146',
    label: 'GDI30L Deep Frozen Dry Ice -70C [+/-10C] Excluding Dry Ice',
  },
  { value: '150', label: 'GDI45L Deep Frozen -70C [+/-10C] with 35kg Dry Ice' },
  { value: '151', label: 'GDI45L Deep Frozen -70C [+/-10C] Excluding Dry Ice' },
  { value: '155', label: 'GDI7L Deep Frozen -70C [+/-10C] with 4kg Dry Ice' },
  { value: '156', label: 'GDI7L Deep Frozen -70C [+/-10C] Excluding Dry Ice' },
  { value: '160', label: 'GDI80L Deep Frozen -70C [+/-10C] with 45kg Dry Ice' },
  { value: '161', label: 'GDI80L Deep Frozen -70C [+/-10C] Excluding Dry Ice' },
  { value: '182', label: 'GTC4L Controlled Ambient +15C to +25C' },
  { value: '183', label: 'GTC4L Frozen -25C to -15C' },
  { value: '185', label: 'GTC4L Refrigerated +2C to +8C' },
];

/** Package name values offered when a temperature has no dedicated SKU list. */
export const WC_FALLBACK_PACKAGE_NAME_VALUES = new Set<string>(['17']);

/**
 * Temperature-range-specific package name values (portal filters options by temperature).
 * Keys are `temperature_range_refrigerant` portal values.
 */
export const WC_TEMPERATURE_PACKAGE_NAMES: Record<string, string[]> = {
  'Controlled Ambient': ['174', '178', '186', '190', '182'],
  Frozen25: ['175', '179', '183', '187', '191'],
  Refrigerated: ['177', '181', '185', '189', '193'],
  Cryogenic: ['89', '448'],
  Deep: ['140', '141', '145', '146', '150', '151', '155', '156', '160', '161'],
  'Dry Ice': [
    '138',
    '139',
    '143',
    '144',
    '148',
    '149',
    '153',
    '154',
    '158',
    '159',
  ],
};

/**
 * Returns all valid package name values for a given temperature range.
 * Temperatures without dedicated SKUs fall back to WC_FALLBACK_PACKAGE_NAME_VALUES.
 */
export function getValidPackageNameValuesForTemperature(
  temperatureRaw: string | null | undefined
): Set<string> {
  const temp = String(temperatureRaw ?? '').trim();
  if (!temp) return new Set();
  const specific = WC_TEMPERATURE_PACKAGE_NAMES[temp];
  if (specific) return new Set(specific);
  return new Set(WC_FALLBACK_PACKAGE_NAME_VALUES);
}

/**
 * Invariant: every value in WC_TEMPERATURE_PACKAGE_NAMES and WC_FALLBACK_PACKAGE_NAME_VALUES
 * must exist in worldCourierPackageNameSelectOptions.
 */
(function assertPackageNameConfiguration() {
  const knownValues = new Set(
    worldCourierPackageNameSelectOptions.map((o) => o.value)
  );
  const referenced = new Set<string>(
    Array.from(WC_FALLBACK_PACKAGE_NAME_VALUES)
  );
  Object.values(WC_TEMPERATURE_PACKAGE_NAMES).forEach((values) =>
    values.forEach((v) => referenced.add(v))
  );
  const missing = Array.from(referenced).filter((v) => !knownValues.has(v));
  if (missing.length > 0) {
    throw new Error(
      `WC package name config references values missing from worldCourierPackageNameSelectOptions: ${missing.join(', ')}`
    );
  }
})();

/**
 * Portal package `item-name` ids where dry ice kg is fixed in the SKU (“with N kg Dry …”).
 * WC omits the separate lbs field; “Select Dry Ice Supply” is still required.
 */
export const WC_PACKAGE_VALUES_WITH_BUNDLED_DRY_ICE = new Set<string>([
  '138',
  '143',
  '148',
  '153',
  '158', // GDI Frozen -80C to -20C with Xkg Dry Ice
  '140',
  '145',
  '150',
  '155',
  '160', // GDI Deep Frozen -70C [+/-10C] with Xkg Dry Ice
]);

/** True when the chosen package bundles dry ice kg in the SKU (portal omits separate lbs field only). */
export function wcPackageHasBundledDryIce(
  packageName: string | null | undefined
): boolean {
  const raw = String(packageName ?? '').trim();
  if (!raw) return false;
  if (WC_PACKAGE_VALUES_WITH_BUNDLED_DRY_ICE.has(raw)) return true;
  const byLabel = worldCourierPackageNameSelectOptions.find(
    (o) => o.value === raw || o.label.trim() === raw.trim()
  );
  if (byLabel && WC_PACKAGE_VALUES_WITH_BUNDLED_DRY_ICE.has(byLabel.value)) {
    return true;
  }
  const lower = raw.toLowerCase();
  if (lower.includes('excluding') && lower.includes('dry')) return false;
  return /\bwith\s+\d+\s*kg\b/.test(lower) && lower.includes('dry');
}
